import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Printer, ShieldCheck } from 'lucide-react';
import { formatCATDateTime } from '../utils/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : '—');
const STATUS_CLS = { issued: 'bg-emerald-100 text-emerald-800', used: 'bg-gray-200 text-gray-700', cancelled: 'bg-red-100 text-red-800', expired: 'bg-amber-100 text-amber-900' };

// Printable pre-authorization form carried by the patient to the pre-auth hospital.
export default function PrintPreauthForm() {
  const { code } = useParams();
  const [doc, setDoc] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    axios.get(`${API}/preauth-codes/${code}`, { withCredentials: true })
      .then((r) => { setDoc(r.data); setState('ok'); })
      .catch((e) => setState(e.response?.status === 401 ? 'login' : 'error'));
  }, [code]);

  if (state === 'login') { window.location.href = '/login'; return null; }
  if (state === 'loading') return <div className="flex items-center justify-center min-h-screen text-gray-600">Loading…</div>;
  if (state === 'error' || !doc) return <div className="flex items-center justify-center min-h-screen text-gray-600">Pre-authorization code not found</div>;

  const Row = ({ label, children }) => (
    <div className="grid grid-cols-[190px_1fr] gap-2 text-sm py-1.5 border-b border-gray-100"><span className="text-gray-600">{label}</span><span className="text-gray-900 whitespace-pre-line [overflow-wrap:anywhere]">{children || '—'}</span></div>
  );

  return (
    <>
      <style>{`@media print { .no-break { break-inside: avoid; } @page { margin: 1.2cm; } .print-color { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>
      <div className="min-h-screen bg-gray-50 p-8 print:p-0 print:bg-white">
        <div className="max-w-3xl mx-auto mb-4 print:hidden flex justify-between items-center bg-white shadow rounded-lg p-4">
          <div className="text-sm text-gray-600"><strong>Pre-authorization Form</strong> — print, stamp and give to the patient to carry to {doc.hospital_name}.</div>
          <div className="flex gap-2">
            <Button onClick={() => window.print()} className="bg-amber-600 hover:bg-amber-700" data-testid="print-preauth-button"><Printer className="w-4 h-4 mr-2" /> Print</Button>
            <Button variant="outline" onClick={() => window.close()}>Close</Button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-lg print:shadow-none p-10 print:p-6" data-testid="preauth-form">
          <div className="flex items-start justify-between border-b-2 border-gray-700 pb-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><ShieldCheck className="w-6 h-6" /> Pre-authorization Form</h1>
              <p className="text-lg text-gray-700 font-medium">Acacia Medical Insurance</p>
              <p className="text-xs text-gray-500">Authorization for treatment at a pre-authorization hospital</p>
            </div>
            <div className="text-right text-sm text-gray-700">
              <p><span className="text-gray-500">Issued</span> {fmtDate(doc.issued_at)}</p>
              <p><span className="text-gray-500">Valid until</span> <strong>{fmtDate(doc.expires_at)}</strong></p>
              <p><span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-bold uppercase print-color ${STATUS_CLS[doc.status] || ''}`} data-testid="preauth-form-status">{doc.status}</span></p>
            </div>
          </div>

          <div className="mb-6 no-break rounded-lg border-2 border-amber-500 bg-amber-50 p-5 text-center print-color" data-testid="preauth-form-code-box">
            <p className="text-xs uppercase tracking-wide text-amber-800 font-semibold">Pre-authorization code — enter at check-in</p>
            <p className="text-4xl font-mono font-bold tracking-[0.3em] text-amber-900 mt-1" data-testid="preauth-form-code">{doc.code}</p>
            <p className="text-xs text-amber-800 mt-1">Single use • Valid only at <strong>{doc.hospital_name}</strong> • Expires {fmtDate(doc.expires_at)}</p>
          </div>

          <div className="mb-6 no-break">
            <h2 className="text-sm font-bold uppercase text-gray-600 mb-1">Patient</h2>
            <Row label="Name"><strong data-testid="preauth-form-patient">{doc.patient_name}</strong></Row>
            <Row label="Serial / Family">{doc.patient_serial_number} • {doc.family_id}</Row>
            <Row label="Principal member">{doc.principal_name}</Row>
            <Row label="Sex / Date of birth / Relationship">{[doc.patient_sex, doc.patient_dob, doc.relationship].filter(Boolean).join(' • ')}</Row>
          </div>

          <div className="mb-6 no-break">
            <h2 className="text-sm font-bold uppercase text-gray-600 mb-1">Authorized treatment</h2>
            <Row label="Hospital"><strong data-testid="preauth-form-hospital">{doc.hospital_name}</strong>{doc.hospital_location ? ` — ${doc.hospital_location}` : ''}</Row>
            <Row label="Reason / treatment"><span data-testid="preauth-form-reason">{doc.reason}</span></Row>
            {doc.notes && <Row label="Notes for the hospital">{doc.notes}</Row>}
            {doc.status === 'used' && <Row label="Used">{doc.used_visit_id} • {formatCATDateTime(doc.used_at)} by {doc.used_by}</Row>}
          </div>

          <div className="mb-8 no-break bg-gray-50 border border-gray-300 rounded-lg p-4 text-sm text-gray-800 print-color">
            <p className="font-semibold mb-1">Instructions to the hospital</p>
            <ol className="list-decimal list-outside ml-5 space-y-0.5">
              <li>Confirm the patient's identity against the serial number above.</li>
              <li>Enter the pre-authorization code in the check-in form (Patient Workflow). The code works once, only for this member and this hospital.</li>
              <li>Do not treat under Acacia cover without a valid, stamped form. Contact the Acacia Office if the code is rejected.</li>
            </ol>
          </div>

          <div className="grid grid-cols-2 gap-8 text-sm text-gray-700 pt-4 no-break items-end">
            <div>
              <p className="text-xs uppercase text-gray-500 mb-1">Issued by Acacia Office</p>
              <p className="text-base font-bold text-gray-900">{doc.issued_by}</p>
              <div className="h-16 border-b border-gray-700 mt-2" />
              <p className="text-xs text-gray-500 mt-1">Acacia Insurance signature & stamp</p>
            </div>
            <div>
              <p className="text-xs uppercase text-gray-500 mb-1">Patient / guardian</p>
              <p className="text-base text-gray-900">{doc.patient_name}</p>
              <div className="h-16 border-b border-gray-700 mt-2" />
              <p className="text-xs text-gray-500 mt-1">Signature</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
